import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus, X, Camera, Tent, Car, Binoculars, MapPin, Check, Loader2,
} from 'lucide-react';
import { WaypointIcon } from '../../types';

const QUICK_ICONS: { icon: WaypointIcon; Icon: React.FC<any>; color: string; label: string }[] = [
  { icon: 'camp',      Icon: Tent,       color: 'text-emerald-500', label: 'Leir' },
  { icon: 'parking',   Icon: Car,        color: 'text-blue-500',    label: 'Parkering' },
  { icon: 'viewpoint', Icon: Binoculars, color: 'text-amber-500',   label: 'Utsikt' },
  { icon: 'pin',       Icon: MapPin,     color: 'text-red-500',     label: 'Punkt' },
];

interface QuickAddFABProps {
  onAddWaypoint: (icon: WaypointIcon) => void;
  onAddPhotoWaypoint: () => void;
  isUploadingPhoto?: boolean;
}

const QuickAddFAB: React.FC<QuickAddFABProps> = ({
  onAddWaypoint,
  onAddPhotoWaypoint,
  isUploadingPhoto = false,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [justAdded, setJustAdded] = useState<WaypointIcon | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleAdd = useCallback((icon: WaypointIcon) => {
    onAddWaypoint(icon);
    setJustAdded(icon);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setJustAdded(null);
      setOpen(false);
    }, 1200);
  }, [onAddWaypoint]);

  const handlePhoto = useCallback(() => {
    onAddPhotoWaypoint();
    setOpen(false);
  }, [onAddPhotoWaypoint]);

  return (
    <div className="absolute bottom-[100px] right-3 z-[3050] pointer-events-auto">
      {/* Expanded menu */}
      {open && (
        <div className="mb-2 flex flex-col items-center gap-1.5 animate-in fade-in slide-in-from-bottom-2 duration-200">
          {/* Photo waypoint button */}
          <button
            onClick={handlePhoto}
            disabled={isUploadingPhoto}
            className="w-12 h-12 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg flex items-center justify-center active:scale-90 transition-all disabled:opacity-50"
            title={t("auto.navigation.quickaddfab.title.ta_bilde", "Ta bilde")}
          >
            {isUploadingPhoto ? <Loader2 size={20} className="animate-spin" /> : <Camera size={20} />}
          </button>

          {/* Quick waypoint icons */}
          {QUICK_ICONS.map(({ icon, Icon, color, label }) => (
            <button
              key={icon}
              onClick={() => handleAdd(icon)}
              className={`w-12 h-12 rounded-2xl shadow-lg flex items-center justify-center active:scale-90 transition-all ${
                justAdded === icon
                  ? 'bg-emerald-100 scale-110'
                  : 'bg-white hover:bg-stone-50'
              }`}
              title={label}
            >
              {justAdded === icon ? (
                <Check size={20} className="text-emerald-500" />
              ) : (
                <Icon size={20} className={color} />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Main FAB */}
      <button
        onClick={() => setOpen(prev => !prev)}
        className={`w-14 h-14 rounded-2xl shadow-2xl flex items-center justify-center transition-all active:scale-90 ${
          open
            ? 'bg-stone-700 hover:bg-stone-800 rotate-45'
            : 'bg-emerald-500 hover:bg-emerald-600'
        }`}
      >
        {open ? (
          <X size={24} className="text-white -rotate-45" />
        ) : (
          <Plus size={24} className="text-white" />
        )}
      </button>
    </div>
  );
};

export default QuickAddFAB;
